import { createFileRoute, Navigate, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Modal, ModalContent } from "@/components/ui/modal";
import { ModalSubmitButton } from "@/components/ui/modal-submit-button";
import { Spinner } from "@/components/ui/spinner";
import { CreateOrganizationForm } from "@/features/onboarding/_components/create-organization-form";
import { StepPage } from "@/features/onboarding/_components/step-page";
import { useMe } from "@/hooks/use-me";
import { useIsMobile } from "@/hooks/use-mobile";
import { BRAND_NAME } from "@/lib/constants/brand";
import { PDPA_CONSENT_VERSION } from "@/lib/pdpa-consent";
import { toast } from "@/lib/toast";

export const Route = createFileRoute("/onboarding/create-organization")({
	component: CreateOrganizationPage,
});

function CreateOrganizationPage() {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const navigate = useNavigate();
	const isMobile = useIsMobile();
	const me = useMe();

	useEffect(() => {
		const flash = sessionStorage.getItem("__flash");
		if (flash === "missing-org") {
			sessionStorage.removeItem("__flash");
			toast.error(<>Finish creating your organization first</>);
		}
	}, []);

	if (me === undefined) {
		return (
			<div className="flex justify-center py-16">
				<Spinner className="size-6" />
			</div>
		);
	}

	if (me?.pdpaConsent?.version !== PDPA_CONSENT_VERSION) {
		return <Navigate to="/onboarding/consent" replace />;
	}

	if (me?.activeOrganizationId) {
		return (
			<Navigate
				to="/onboarding/create-branch"
				search={{ org: me.activeOrganizationId }}
				replace
			/>
		);
	}

	const footer = (
		<ModalSubmitButton
			label="Create organization"
			formId="create-organization-form"
			isSubmitting={isSubmitting}
			className="ml-auto"
			shortcut="enter"
		/>
	);

	const mobileFooter = (
		<div className="fixed bottom-0 left-0 right-0 bg-neutral-50 border-t px-6 py-2 flex justify-end z-10">
			<ModalSubmitButton
				label="Create organization"
				formId="create-organization-form"
				isSubmitting={isSubmitting}
			/>
		</div>
	);

	const page = (
		<StepPage
			title="Create your organization"
			description={`Set up your business on ${BRAND_NAME}. You can always change these settings later.`}
			className={
				isMobile
					? "w-full h-full rounded-none! ring-0! overflow-y-auto"
					: "max-w-lg"
			}
			footer={isMobile ? undefined : footer}
		>
			<CreateOrganizationForm
				hideSubmit
				formId="create-organization-form"
				onSubmittingChange={setIsSubmitting}
			/>
		</StepPage>
	);

	const handleClose = () => {
		navigate({ to: "/" });
	};

	if (isMobile) {
		return (
			<Modal open={true} onOpenChange={(o) => !o && handleClose()}>
				<ModalContent className="p-0 gap-0 overflow-hidden h-full">
					<div className="h-full overflow-y-auto pb-20">{page}</div>
					{mobileFooter}
				</ModalContent>
			</Modal>
		);
	}

	return page;
}
